import type { WorkflowRecord } from '../../types'
import type {
  ConfiguredFieldsBaseValues,
  ConfiguredFieldState,
  ConfiguredFieldsPayItem,
  ConfiguredFieldsContext,
} from './types'

/**
 * Reconstructs initialization values and validation metadata from the record.
 *
 * The mutation still reloads and validates the locked raw values. These values
 * only preserve form context while an action overlay is open.
 */
export function configuredFieldsRecordContext(
  record: Pick<WorkflowRecord, 'values'>
): Pick<
  ConfiguredFieldsContext,
  'approvalFields' | 'baseValues' | 'initialValueContext'
> {
  const values: ConfiguredFieldsBaseValues = {}
  const approvalFields: ConfiguredFieldState[] = []
  const payItems: ConfiguredFieldsPayItem[] = []
  for (const fieldValue of Object.values(record.values)) {
    if (fieldValue.type === 'long_text')
      values[fieldValue.fieldId] = fieldValue.value
    else if (fieldValue.type === 'pay_items') {
      values[fieldValue.fieldId] = fieldValue.value.map(
        ({ payItemId }) => payItemId
      )
      payItems.push(
        ...fieldValue.value.map(
          ({ payItemId, payItemDeleted, payItemLabel }) => ({
            globalId: payItemId,
            deleted: payItemDeleted,
            label: payItemLabel,
          })
        )
      )
    } else if (fieldValue.type === 'select')
      values[fieldValue.fieldId] = fieldValue.value.map(
        ({ optionId }) => optionId
      )
    else if (fieldValue.type === 'approvals') {
      values[fieldValue.fieldId] = fieldValue.value
        .filter(({ optionDeleted }) => !optionDeleted)
        .map(({ optionId }) => ({
          optionId,
          state: null,
        }))
      approvalFields.push({
        fieldId: fieldValue.fieldId,
        options: fieldValue.value.map(({ optionId, state }) => ({
          optionId,
          state: state?.type ?? null,
        })),
      })
    } else if (fieldValue.type === 'selected_by')
      values[fieldValue.fieldId] = fieldValue.value
        ? {
            optionId: fieldValue.value.optionId,
            byId: fieldValue.value.selectedById,
            date: new Date(fieldValue.value.selectedDate),
          }
        : null
    else if (fieldValue.type === 'comments') values[fieldValue.fieldId] = []
  }
  return {
    approvalFields,
    baseValues: values,
    initialValueContext: { payItems },
  }
}
